/**
 * הצגת הצעת מחיר ללקוח — טקסט קבוע ושדות שנשמרים עם ההצעה.
 */
import { resolveCustomerFacingOfferNumber } from "./resolveCustomerFacingOfferNumber";

export const CUSTOMER_QUOTE_USD_DISCLAIMER_HE =
  "המחיר נכון לשער הדולר ברגע הבדיקה ועלול להשתנות";

/**
 * בונה אובייקט לשמירה ב-create/update של הצעת לקוח (בתוך ourPrice + שורש הבקשה).
 * @param {object} params
 * @param {string|number|null} params.dealId — מספר הצעה כפי שמוצג לסוכן (dealId)
 * @param {string|null} params.savedOrderId — מזהה הזמנה אם כבר קיים
 * @param {number|null} params.priceIls — סה״כ לתשלום בש״ח (priceWithCreditCardFee)
 * @param {(n:number)=>number} params.ilsToUsd — כמו ב-SelectedResultContext
 */
export function buildCustomerQuotePersistPayload({
  dealId,
  savedOrderId,
  priceIls,
  ilsToUsd,
  /** שם פנייה בהצעה (נשמר ב-customer_quote_display לשרת / טעינה חוזרת) */
  customerGreetingName = null,
  /** ל־CRM — שם פרטי / משפחה (אופציונלי) */
  customerFirstName = null,
  customerLastName = null,
}) {
  const ils = Number(priceIls);
  let totalPriceUsdSnapshot = null;
  if (typeof ilsToUsd === "function" && Number.isFinite(ils) && ils > 0) {
    const u = ilsToUsd(ils);
    if (typeof u === "number" && Number.isFinite(u) && u > 0) {
      totalPriceUsdSnapshot = u;
    }
  }
  const dealStr =
    dealId != null && String(dealId).trim() !== ""
      ? String(dealId).trim()
      : null;
  const orderStr =
    savedOrderId != null && String(savedOrderId).trim() !== ""
      ? String(savedOrderId).trim()
      : null;
  const offerNumber = dealStr || orderStr || null;
  const greeting =
    customerGreetingName != null && String(customerGreetingName).trim()
      ? String(customerGreetingName).trim().slice(0, 120)
      : null;
  const first =
    customerFirstName != null && String(customerFirstName).trim()
      ? String(customerFirstName).trim().slice(0, 60)
      : null;
  const last =
    customerLastName != null && String(customerLastName).trim()
      ? String(customerLastName).trim().slice(0, 60)
      : null;

  return {
    customerQuoteDisplay: {
      offerNumber,
      totalPriceUsdSnapshot,
      displayPriceDisclaimer: CUSTOMER_QUOTE_USD_DISCLAIMER_HE,
      ...(greeting ? { customerGreetingName: greeting } : {}),
      ...(first ? { customerFirstName: first } : {}),
      ...(last ? { customerLastName: last } : {}),
    },
    customer_quote_display: {
      offer_number: offerNumber,
      total_price_usd: totalPriceUsdSnapshot,
      display_price_disclaimer: CUSTOMER_QUOTE_USD_DISCLAIMER_HE,
      ...(greeting ? { customer_greeting_name: greeting } : {}),
      ...(first ? { customer_first_name: first } : {}),
      ...(last ? { customer_last_name: last } : {}),
    },
  };
}

function toUsdNumber(value) {
  if (value == null) return null;
  const n =
    typeof value === "string" ? parseFloat(String(value).replace(/,/g, "")) : Number(value);
  return typeof n === "number" && !Number.isNaN(n) && n > 0 ? n : null;
}

/**
 * חילוץ USD ומספר הצעה מההזמנה שנטענה — סדר עדיפויות לשמירה מול fallback.
 */
export function resolveCustomerQuoteUsdAndOfferFromOrder(order, { ilsToUsd, contextOurPrice }) {
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const rootCqd = order?.customer_quote_display ?? order?.customerQuoteDisplay;

  let usd = null;
  let usdSource = null;

  const tryUsd = (v, src) => {
    const n = toUsdNumber(v);
    if (n != null) {
      usd = n;
      usdSource = src;
      return true;
    }
    return false;
  };

  tryUsd(rootCqd?.total_price_usd, "saved: order.customer_quote_display.total_price_usd");
  if (!usd)
    tryUsd(
      op?.customer_quote_display?.total_price_usd,
      "saved: ourprice.customer_quote_display.total_price_usd"
    );
  if (!usd)
    tryUsd(
      op?.customerQuoteDisplay?.totalPriceUsdSnapshot,
      "saved: ourprice.customerQuoteDisplay.totalPriceUsdSnapshot"
    );
  if (!usd) tryUsd(op?.priceWithCreditCardFeeUsd, "saved: ourprice.priceWithCreditCardFeeUsd");
  if (!usd) tryUsd(op?.priceInUsd, "saved: ourprice.priceInUsd");
  if (!usd) tryUsd(op?.usd, "saved: ourprice.usd");
  if (!usd) {
    const ils = Number(op?.priceWithCreditCardFee ?? contextOurPrice?.priceWithCreditCardFee);
    if (typeof ilsToUsd === "function" && Number.isFinite(ils) && ils > 0) {
      const fb = ilsToUsd(ils);
      if (typeof fb === "number" && Number.isFinite(fb) && fb > 0) {
        const hadAnySavedHint =
          rootCqd != null ||
          op?.customerQuoteDisplay != null ||
          op?.customer_quote_display != null ||
          toUsdNumber(op?.priceWithCreditCardFeeUsd) != null;
        usd = fb;
        usdSource = hadAnySavedHint
          ? "fallback: ilsToUsd (saved USD missing or invalid)"
          : "fallback: ilsToUsd(priceWithCreditCardFee) — no snapshot on order";
        console.warn("[customerQuoteDisplay] USD fallback used:", {
          source: usdSource,
          ils,
          usd: fb,
          orderId: order?.id,
        });
      }
    }
  }

  const facing = resolveCustomerFacingOfferNumber(order);
  const offerNumber = facing.show ? facing.value : null;

  const disclaimer =
    op?.customerQuoteDisplay?.displayPriceDisclaimer ??
    op?.customer_quote_display?.display_price_disclaimer ??
    rootCqd?.display_price_disclaimer ??
    CUSTOMER_QUOTE_USD_DISCLAIMER_HE;

  return {
    usd,
    usdSource: usdSource || (usd ? "unknown" : null),
    offerNumber: offerNumber || null,
    disclaimerHe: disclaimer || CUSTOMER_QUOTE_USD_DISCLAIMER_HE,
  };
}
